import { useState, useEffect } from 'react';
import {
  submodalidadesVisuais,
  submodalidadesAuditivas,
  submodalidadesCinestesicas,
  exemplosExperiencias,
  dicasSubmodalidades
} from '../../../mocks/worksheet-submodalidades';

interface SubmodalidadeValues {
  [key: string]: number;
}

interface ExperienciaData {
  experienciaNegativa: string;
  experienciaPositiva: string;
  visuaisNegativas: SubmodalidadeValues;
  auditivasNegativas: SubmodalidadeValues;
  cinestesicasNegativas: SubmodalidadeValues;
  visuaisPositivas: SubmodalidadeValues;
  auditivasPositivas: SubmodalidadeValues;
  cinestesicasPositivas: SubmodalidadeValues;
  driversIdentificados: string;
  testeFinal: string;
}

export default function PraticaSubmodalidades() {
  const [experienciaAtual, setExperienciaAtual] = useState(0);
  const [experiencias, setExperiencias] = useState<ExperienciaData[]>([
    {
      experienciaNegativa: '',
      experienciaPositiva: '',
      visuaisNegativas: {},
      auditivasNegativas: {},
      cinestesicasNegativas: {},
      visuaisPositivas: {},
      auditivasPositivas: {},
      cinestesicasPositivas: {},
      driversIdentificados: '',
      testeFinal: ''
    }
  ]);
  const [dicaAleatoria] = useState(dicasSubmodalidades[Math.floor(Math.random() * dicasSubmodalidades.length)]);

  useEffect(() => {
    const saved = localStorage.getItem('worksheet-submodalidades');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setExperiencias(data.experiencias || experiencias);
        setExperienciaAtual(data.experienciaAtual || 0);
      } catch (e) {
        console.error('Erro ao carregar dados salvos', e);
      }
    }
  }, []);

  useEffect(() => {
    const autoSave = setInterval(() => {
      localStorage.setItem('worksheet-submodalidades', JSON.stringify({
        experiencias,
        experienciaAtual,
        lastSaved: new Date().toISOString()
      }));
    }, 30000);

    return () => clearInterval(autoSave);
  }, [experiencias, experienciaAtual]);

  const updateExperiencia = (field: keyof ExperienciaData, value: any) => {
    const newExperiencias = [...experiencias];
    newExperiencias[experienciaAtual] = {
      ...newExperiencias[experienciaAtual],
      [field]: value
    };
    setExperiencias(newExperiencias);
  };

  const updateSubmodalidade = (tipo: 'visuaisNegativas' | 'auditivasNegativas' | 'cinestesicasNegativas' | 'visuaisPositivas' | 'auditivasPositivas' | 'cinestesicasPositivas', nome: string, valor: number) => {
    const newExperiencias = [...experiencias];
    newExperiencias[experienciaAtual] = {
      ...newExperiencias[experienciaAtual],
      [tipo]: {
        ...newExperiencias[experienciaAtual][tipo],
        [nome]: valor
      }
    };
    setExperiencias(newExperiencias);
  };

  const adicionarExperiencia = () => {
    if (experiencias.length < 5) {
      setExperiencias([...experiencias, {
        experienciaNegativa: '',
        experienciaPositiva: '',
        visuaisNegativas: {},
        auditivasNegativas: {},
        cinestesicasNegativas: {},
        visuaisPositivas: {},
        auditivasPositivas: {},
        cinestesicasPositivas: {},
        driversIdentificados: '',
        testeFinal: ''
      }]);
      setExperienciaAtual(experiencias.length);
    }
  };

  const carregarExemplo = () => {
    const exemplo = exemplosExperiencias[Math.floor(Math.random() * exemplosExperiencias.length)];
    updateExperiencia('experienciaNegativa', exemplo.negativa);
    updateExperiencia('experienciaPositiva', exemplo.positiva);
  };

  const aplicarMappingAcross = () => {
    const exp = experiencias[experienciaAtual];
    updateExperiencia('visuaisNegativas', { ...exp.visuaisPositivas });
    updateExperiencia('auditivasNegativas', { ...exp.auditivasPositivas });
    updateExperiencia('cinestesicasNegativas', { ...exp.cinestesicasPositivas });
  };

  const calcularProgresso = () => {
    const exp = experiencias[experienciaAtual];
    let pontos = 0;
    
    if (exp.experienciaNegativa.length > 10) pontos += 15;
    if (exp.experienciaPositiva.length > 10) pontos += 15;
    if (Object.keys(exp.visuaisNegativas).length >= 3) pontos += 15;
    if (Object.keys(exp.auditivasNegativas).length >= 2) pontos += 10;
    if (Object.keys(exp.cinestesicasNegativas).length >= 2) pontos += 10;
    if (Object.keys(exp.visuaisPositivas).length >= 3) pontos += 15;
    if (exp.driversIdentificados.length > 10) pontos += 10;
    if (exp.testeFinal.length > 10) pontos += 10;
    
    return pontos;
  };

  const exp = experiencias[experienciaAtual];
  const progresso = calcularProgresso();

  return (
    <section id="pratica-interativa" className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Prática Interativa
          </h2>
          <p className="text-lg text-gray-600">
            Mapeie e transforme suas experiências ajustando as submodalidades
          </p>
        </div>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-2">
            {experiencias.map((_, index) => (
              <button
                key={index}
                onClick={() => setExperienciaAtual(index)}
                className={`w-10 h-10 rounded-full font-semibold transition-all whitespace-nowrap flex items-center justify-center ${
                  experienciaAtual === index
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-110'
                    : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-indigo-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
            {experiencias.length < 5 && (
              <button
                onClick={adicionarExperiencia}
                className="w-10 h-10 rounded-full border-2 border-dashed border-gray-300 text-gray-400 hover:border-indigo-400 hover:text-indigo-600 transition-all whitespace-nowrap flex items-center justify-center"
              >
                <i className="ri-add-line text-xl"></i>
              </button>
            )}
          </div>

          <button
            onClick={carregarExemplo}
            className="px-4 py-2 bg-white border-2 border-indigo-200 text-indigo-600 rounded-full text-sm font-semibold hover:bg-indigo-50 transition-all whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-lightbulb-line"></i>
            Carregar Exemplo
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Progresso desta experiência</span>
              <span className="text-sm font-bold text-indigo-600">{progresso}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full transition-all duration-500"
                style={{ width: `${progresso}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-500 p-4 rounded-lg mb-8">
            <div className="flex items-start gap-3">
              <i className="ri-lightbulb-flash-line text-indigo-600 text-xl flex-shrink-0 mt-1"></i>
              <p className="text-sm text-indigo-900">{dicaAleatoria}</p>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-sm">1</span>
                Escolher Experiências
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Experiência Negativa que deseja transformar
                  </label>
                  <textarea
                    value={exp.experienciaNegativa}
                    onChange={(e) => updateExperiencia('experienciaNegativa', e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Ex: Ansiedade antes de apresentações públicas..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{exp.experienciaNegativa.length}/500 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Experiência de Recurso Positivo (para comparação)
                  </label>
                  <textarea
                    value={exp.experienciaPositiva}
                    onChange={(e) => updateExperiencia('experienciaPositiva', e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Ex: Confiança ao conversar com amigos próximos..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{exp.experienciaPositiva.length}/500 caracteres</p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h4 className="text-lg font-bold text-red-600 flex items-center gap-2">
                  <i className="ri-close-circle-line"></i>
                  Experiência Negativa
                </h4>

                <div>
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-eye-line text-blue-600"></i>
                    Submodalidades Visuais
                  </h5>
                  <div className="space-y-4">
                    {submodalidadesVisuais.map((sub) => (
                      <div key={sub.nome}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">{sub.nome}</label>
                          <span className="text-xs text-gray-500">{exp.visuaisNegativas[sub.nome] || 50}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={exp.visuaisNegativas[sub.nome] || 50}
                          onChange={(e) => updateSubmodalidade('visuaisNegativas', sub.nome, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{sub.min}</span>
                          <span>{sub.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-volume-up-line text-green-600"></i>
                    Submodalidades Auditivas
                  </h5>
                  <div className="space-y-4">
                    {submodalidadesAuditivas.map((sub) => (
                      <div key={sub.nome}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">{sub.nome}</label>
                          <span className="text-xs text-gray-500">{exp.auditivasNegativas[sub.nome] || 50}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={exp.auditivasNegativas[sub.nome] || 50}
                          onChange={(e) => updateSubmodalidade('auditivasNegativas', sub.nome, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{sub.min}</span>
                          <span>{sub.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-hand-heart-line text-orange-600"></i>
                    Submodalidades Cinestésicas
                  </h5>
                  <div className="space-y-4">
                    {submodalidadesCinestesicas.map((sub) => (
                      <div key={sub.nome}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">{sub.nome}</label>
                          <span className="text-xs text-gray-500">{exp.cinestesicasNegativas[sub.nome] || 50}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={exp.cinestesicasNegativas[sub.nome] || 50}
                          onChange={(e) => updateSubmodalidade('cinestesicasNegativas', sub.nome, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{sub.min}</span>
                          <span>{sub.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h4 className="text-lg font-bold text-green-600 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  Experiência Positiva
                </h4>

                <div>
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-eye-line text-blue-600"></i>
                    Submodalidades Visuais
                  </h5>
                  <div className="space-y-4">
                    {submodalidadesVisuais.map((sub) => (
                      <div key={sub.nome}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">{sub.nome}</label>
                          <span className="text-xs text-gray-500">{exp.visuaisPositivas[sub.nome] || 50}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={exp.visuaisPositivas[sub.nome] || 50}
                          onChange={(e) => updateSubmodalidade('visuaisPositivas', sub.nome, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{sub.min}</span>
                          <span>{sub.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-volume-up-line text-green-600"></i>
                    Submodalidades Auditivas
                  </h5>
                  <div className="space-y-4">
                    {submodalidadesAuditivas.map((sub) => (
                      <div key={sub.nome}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">{sub.nome}</label>
                          <span className="text-xs text-gray-500">{exp.auditivasPositivas[sub.nome] || 50}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={exp.auditivasPositivas[sub.nome] || 50}
                          onChange={(e) => updateSubmodalidade('auditivasPositivas', sub.nome, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{sub.min}</span>
                          <span>{sub.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-hand-heart-line text-orange-600"></i>
                    Submodalidades Cinestésicas
                  </h5>
                  <div className="space-y-4">
                    {submodalidadesCinestesicas.map((sub) => (
                      <div key={sub.nome}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">{sub.nome}</label>
                          <span className="text-xs text-gray-500">{exp.cinestesicasPositivas[sub.nome] || 50}</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={exp.cinestesicasPositivas[sub.nome] || 50}
                          onChange={(e) => updateSubmodalidade('cinestesicasPositivas', sub.nome, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{sub.min}</span>
                          <span>{sub.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={aplicarMappingAcross}
                className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-arrow-left-right-line text-xl"></i>
                Aplicar Mapping Across
              </button>
              <p className="text-sm text-gray-600 mt-2">Transferir qualidades da experiência positiva para a negativa</p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-sm">5</span>
                Drivers Críticos Identificados
              </h3>
              <textarea
                value={exp.driversIdentificados}
                onChange={(e) => updateExperiencia('driversIdentificados', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Quais submodalidades tiveram maior impacto emocional ao serem alteradas?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{exp.driversIdentificados.length}/500 caracteres</p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-gradient-to-br from-teal-500 to-green-500 rounded-lg flex items-center justify-center text-white text-sm">8</span>
                Teste do Resultado
              </h3>
              <textarea
                value={exp.testeFinal}
                onChange={(e) => updateExperiencia('testeFinal', e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Como você se sente agora ao pensar na experiência original? A carga emocional diminuiu?"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{exp.testeFinal.length}/500 caracteres</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}